import { Button, Checkbox, DialogV2, TextArea, Typography } from '@recruit-robin/robin-theme/web-components';
import { Question, Send_01 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useEffect, useState } from 'react';

import {
  useRejectCandidateMutation,
  useRejectNotQualifiedCandidateMutation,
} from '@/features/recruiter-rejects-candidate';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { RejectionType } from 'types/rejection';
import { useStyles } from './RejectCandidateModal.styles';

export const RejectCandidateModal = () => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const rejectCandidateDialog = useGlobalDialog(GlobalDialogType.RejectCandidate);
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.RejectCandidate);
  const dialogData = useGlobalDialogData({ dialogType: GlobalDialogType.RejectCandidate, isVisible });
  const { mutateAsync: rejectAppliedCandidate, isLoading: isRejectingApplied } = useRejectCandidateMutation();
  const { mutateAsync: rejectNotQualifiedCandidate, isLoading: isRejectingNotQualified } =
    useRejectNotQualifiedCandidateMutation();
  const isLoading = isRejectingApplied || isRejectingNotQualified;

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (!isVisible) {
      setSelectedReasons([]);
      setFeedback('');
    }
  }, [isVisible]);

  const reasonOptions = [
    dictionary.rejectCandidateReasonNotEnoughExperience,
    dictionary.rejectCandidateReasonMissingCertificate,
    dictionary.rejectCandidateReasonNotAvailableForHours,
    dictionary.rejectCandidateReasonLivesTooFar,
    dictionary.rejectCandidateReasonPositionFilled,
    dictionary.rejectCandidateReasonOther,
  ];

  if (!dialogData) return null;

  const handleClose = () => {
    rejectCandidateDialog.close();
  };

  const isOtherSelected = selectedReasons.includes(dictionary.rejectCandidateReasonOther);

  const handleCheckboxChange = (reason: string, checked: boolean) => {
    setSelectedReasons((prev) => (checked ? [...prev, reason] : prev.filter((r) => r !== reason)));
    if (reason === dictionary.rejectCandidateReasonOther && !checked) {
      setFeedback('');
    }
  };

  const handleSubmit = async () => {
    const parts = [...selectedReasons];
    if (feedback.trim()) parts.push(feedback.trim());
    const reason = parts.join(', ');

    if (dialogData.rejectionType === RejectionType.NotQualified) {
      await rejectNotQualifiedCandidate({
        esPersonId: dialogData.esPersonId,
        campaignId: dialogData.campaignId,
        reason: selectedReasons.join(', '),
        feedback: feedback.trim(),
        isPositionFilled: selectedReasons.includes(dictionary.rejectCandidateReasonPositionFilled),
      });
    } else {
      await rejectAppliedCandidate({
        esPersonId: dialogData.esPersonId,
        campaignId: dialogData.campaignId,
        reason,
        improvedJobId: dialogData.improvedJobId,
      });
    }
    dialogData.onSuccess?.();
    handleClose();
  };

  const isSubmitDisabled = selectedReasons.length === 0 || (isOtherSelected && !feedback.trim()) || isLoading;

  return (
    <DialogV2.Root isVisible={isVisible} customCss={{ zIndex: 3 }}>
      <DialogV2.Backdrop />

      <DialogV2.Content customCss={classes.modalContent}>
        <DialogV2.Body customCss={classes.body}>
          <div css={classes.header}>
            <div css={classes.iconContainer}>
              <Question size={24} />
            </div>
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {dictionary.rejectCandidateModalTitle}
            </Typography>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[60]}>
              {dictionary.rejectCandidateModalDescription}
            </Typography>
          </div>

          <div css={classes.checkboxGroup}>
            {reasonOptions.map((reason) => (
              <Checkbox
                key={reason}
                label={reason}
                checked={selectedReasons.includes(reason)}
                onClick={(checked) => handleCheckboxChange(reason, checked)}
              />
            ))}
          </div>

          {isOtherSelected && (
            <TextArea
              placeholder={dictionary.rejectCandidateCustomReasonPlaceholder}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              customCss={classes.textarea}
              rows={4}
              disableResize
            />
          )}

          <div css={classes.actions}>
            <button css={classes.cancelButton} onClick={handleClose}>
              {dictionary.cancel}
            </button>
            <Button
              style="filled"
              type="success"
              onClick={handleSubmit}
              label={dictionary.rejectCandidateSubmit}
              endIcon={<Send_01 size={16} />}
              disabled={isSubmitDisabled}
            />
          </div>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};
